import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './PendingMessages.css';
import { formatDateTime } from '../utils/dates';
import { decodeText } from '../utils/maintenanceText';
import { usePagination, SeeMoreButton } from './usePagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_KEY = 'Bearer'; // Step 41 — auth via httpOnly cookie; this stub keeps existing headers harmless

// Map legacy technical flag codes to plain English
const FLAG_REASON_MAP = {
  'E1': 'Message contains a legal keyword',
  'E2': 'Tenant has an active Section 8 case',
  'E3': 'Tenant arrears exceed the 2-month threshold',
  'E4': 'A previous message with this tenant was flagged for review',
  'E5': 'First ever message to this tenant — manual review required',
  'E6': 'Tenant is requesting to speak to a person',
  'E7': 'You are in Draft and Approve mode — all messages require your approval',
  'E8': 'Active Section 8 case — all communications held for review',
  'OVERRIDE 1': 'Section 8 case at Stage 3 or above — draft-and-approve enforced',
  'OVERRIDE 2': 'First ever contact with this tenant — draft-and-approve enforced',
  'OVERRIDE 3': 'An escalation rule was triggered — message held for your review',
};

const humaniseFlagReason = (raw) => {
  if (!raw) return '';
  return raw.split('; ').map(part => {
    const code = Object.keys(FLAG_REASON_MAP).find(k => part.startsWith(k + ':') || part.startsWith(k + ' '));
    return code ? FLAG_REASON_MAP[code] : part;
  }).join('; ');
};

const PendingMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // 'pending', 'approved', 'rejected', 'all'
  const [editingMessage, setEditingMessage] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [processing, setProcessing] = useState(null); // message_id being processed

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/pending-messages/all`, {
        headers: { 'Authorization': API_KEY }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch (err) {
      console.error('Error fetching pending messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (msg) => {
    const messageId = msg._id;
    setProcessing(messageId);
    try {
      // 2026-02 (Bug 2 refix v4) — Route to the maintenance
      // module's approve endpoint for MR-forward rows; the
      // standard endpoint expects a tenant recipient.
      const url = msg.source_type === 'maintenance_forward'
        ? `${BACKEND_URL}/api/maintenance/${msg.maintenance_request_id}/pending-messages/${messageId}/approve`
        : `${BACKEND_URL}/api/pending-message/${messageId}/approve`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': API_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (res.ok) {
        await fetchMessages();
        window.dispatchEvent(new Event('eiq-alerts-refresh'));
      }
    } catch (err) {
      console.error('Error approving message:', err);
    } finally {
      setProcessing(null);
    }
  };

  const handleEditAndApprove = async (msg) => {
    const messageId = msg._id;
    setProcessing(messageId);
    try {
      // For maintenance forwards: PATCH the draft via /edit, then
      // hit /approve (which reads the row and sends). For standard
      // pending messages: single-call approve with edited body.
      if (msg.source_type === 'maintenance_forward') {
        const eRes = await fetch(
          `${BACKEND_URL}/api/maintenance/${msg.maintenance_request_id}/pending-messages/${messageId}/edit`,
          {
            method: 'POST',
            headers: { 'Authorization': API_KEY, 'Content-Type': 'application/json' },
            body: JSON.stringify({ draft_body: editContent }),
          },
        );
        if (!eRes.ok) throw new Error(`edit failed: ${eRes.status}`);
        const aRes = await fetch(
          `${BACKEND_URL}/api/maintenance/${msg.maintenance_request_id}/pending-messages/${messageId}/approve`,
          {
            method: 'POST',
            headers: { 'Authorization': API_KEY, 'Content-Type': 'application/json' },
            body: JSON.stringify({}),
          },
        );
        if (!aRes.ok) throw new Error(`approve failed: ${aRes.status}`);
        setEditingMessage(null);
        setEditContent('');
        await fetchMessages();
        window.dispatchEvent(new Event('eiq-alerts-refresh'));
        return;
      }
      const res = await fetch(`${BACKEND_URL}/api/pending-message/${messageId}/approve`, {
        method: 'POST',
        headers: { 'Authorization': API_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({ edited_content_text: editContent })
      });
      if (res.ok) {
        setEditingMessage(null);
        setEditContent('');
        await fetchMessages();
        window.dispatchEvent(new Event('eiq-alerts-refresh'));
      }
    } catch (err) {
      console.error('Error editing and approving:', err);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (msg) => {
    const messageId = msg._id;
    if (!window.confirm('Reject this message? It will NOT be sent.')) return;
    setProcessing(messageId);
    try {
      // Maintenance-forward rows use the module's `/dismiss`
      // endpoint (marks status=dismissed on the MR-embedded row).
      const url = msg.source_type === 'maintenance_forward'
        ? `${BACKEND_URL}/api/maintenance/${msg.maintenance_request_id}/pending-messages/${messageId}/dismiss`
        : `${BACKEND_URL}/api/pending-message/${messageId}/reject`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': API_KEY, 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        await fetchMessages();
        window.dispatchEvent(new Event('eiq-alerts-refresh'));
      }
    } catch (err) {
      console.error('Error rejecting message:', err);
    } finally {
      setProcessing(null);
    }
  };

  const openEditor = (msg) => {
    setEditingMessage(msg._id);
    setEditContent(decodeText(msg.drafted_content_text));
  };

  const filteredMessages = messages.filter(m => {
    if (filter === 'all') return true;
    return m.status === filter;
  });

  // 2026-02 FIX 1 — 25-per-page pagination on the full pending list.
  const pmPager = usePagination(filteredMessages, 25);
  const pmVisibleMessages = pmPager.visible;

  const pendingCount = messages.filter(m => m.status === 'pending').length;
  const approvedCount = messages.filter(m => m.status === 'approved' || m.status === 'edited_and_approved').length;
  const rejectedCount = messages.filter(m => m.status === 'rejected').length;

  const formatDate = (dateStr) => formatDateTime(dateStr, 'N/A');

  const getStatusBadgeClass = (s) => {
    if (s === 'pending') return 'pm-badge-pending';
    if (s === 'approved' || s === 'edited_and_approved') return 'pm-badge-approved';
    if (s === 'rejected') return 'pm-badge-rejected';
    return '';
  };

  if (loading) {
    return <div className="pm-container"><div className="pm-loading">Loading pending messages...</div></div>;
  }

  return (
    <div className="pm-container" data-testid="pending-messages-page">
      <div className="pm-header">
        <div>
          <h1 className="pm-title">Message Approval Queue</h1>
          <p className="pm-subtitle">
            {pendingCount} pending &middot; {approvedCount} approved &middot; {rejectedCount} rejected
          </p>
        </div>
        <Link to="/" className="pm-back-link" data-testid="pm-back-to-dashboard">Back to Dashboard</Link>
      </div>

      {/* Filter Tabs */}
      <div className="pm-filters" data-testid="pm-filters">
        <button className={`pm-filter-btn ${filter === 'pending' ? 'active' : ''}`} onClick={() => setFilter('pending')} data-testid="pm-filter-pending">
          Pending ({pendingCount})
        </button>
        <button className={`pm-filter-btn ${filter === 'approved' ? 'active' : ''}`} onClick={() => setFilter('approved')} data-testid="pm-filter-approved">
          Approved ({approvedCount})
        </button>
        <button className={`pm-filter-btn ${filter === 'rejected' ? 'active' : ''}`} onClick={() => setFilter('rejected')} data-testid="pm-filter-rejected">
          Rejected ({rejectedCount})
        </button>
        <button className={`pm-filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')} data-testid="pm-filter-all">
          All ({messages.length})
        </button>
      </div>

      {/* Messages List */}
      {filteredMessages.length === 0 ? (
        <div className="pm-empty" data-testid="pm-empty-state">
          <p>No {filter === 'all' ? '' : filter} messages found.</p>
        </div>
      ) : (
        <div className="pm-list">
          {pmVisibleMessages.map((msg) => (
            <div key={msg._id} className={`pm-card ${msg.status === 'pending' ? 'pm-card-pending' : ''}`} data-testid={`pm-card-${msg._id}`}>
              <div className="pm-card-top">
                <div className="pm-card-meta">
                  <span className={`pm-status-badge ${getStatusBadgeClass(msg.status)}`} data-testid={`pm-status-${msg._id}`}>
                    {msg.status === 'edited_and_approved' ? 'Edited & Approved' : msg.status}
                  </span>
                  <span className="pm-channel-badge">{msg.channel}</span>
                  {msg.hours_pending && msg.status === 'pending' && (
                    <span className={`pm-time-badge ${msg.hours_pending >= 24 ? 'pm-urgent' : msg.hours_pending >= 4 ? 'pm-warning' : ''}`}>
                      {msg.hours_pending < 1 ? '<1h' : `${Math.floor(msg.hours_pending)}h`} pending
                    </span>
                  )}
                </div>
                <span className="pm-date">{formatDate(msg.created_at)}</span>
              </div>

              <div className="pm-card-body">
                <div className="pm-tenant-info">
                  <Link to={`/tenants/${msg.tenant_id_str}`} className="pm-tenant-link" data-testid={`pm-tenant-link-${msg._id}`}>
                    {msg.tenant_name}
                  </Link>
                  {msg.property_address && <span className="pm-property">{msg.property_address}</span>}
                </div>

                {msg.drafted_subject && (
                  <div className="pm-subject">Subject: {msg.drafted_subject}</div>
                )}

                <div className="pm-flag-reason" data-testid={`pm-flag-reason-${msg._id}`}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>
                  {humaniseFlagReason(msg.flag_reason)}
                </div>

                {/* Show content — inline editor if editing */}
                {editingMessage === msg._id ? (
                  <div className="pm-editor" data-testid={`pm-editor-${msg._id}`}>
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      rows={6}
                      className="pm-editor-textarea"
                      data-testid={`pm-editor-textarea-${msg._id}`}
                    />
                    <div className="pm-editor-locked">
                      <small>Sender name, sign-off phrase, and email signature are locked and will be appended automatically.</small>
                    </div>
                    <div className="pm-editor-actions">
                      <button
                        className="pm-btn pm-btn-primary"
                        onClick={() => handleEditAndApprove(msg)}
                        disabled={processing === msg._id || !editContent.trim()}
                        data-testid={`pm-btn-submit-edit-${msg._id}`}
                      >
                        {processing === msg._id ? 'Sending...' : 'Send Edited Version'}
                      </button>
                      <button className="pm-btn pm-btn-ghost" onClick={() => { setEditingMessage(null); setEditContent(''); }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="pm-content-preview" data-testid={`pm-content-${msg._id}`}>
                    {decodeText(msg.drafted_content_text)}
                  </div>
                )}

                {/* Approved/rejected info */}
                {msg.status !== 'pending' && msg.approved_by && (
                  <div className="pm-resolution-info">
                    {msg.status === 'rejected' ? 'Rejected' : 'Approved'} by <strong>{msg.approved_by}</strong> at {formatDate(msg.approved_at)}
                  </div>
                )}
              </div>

              {/* Action buttons — only for pending messages */}
              {msg.status === 'pending' && editingMessage !== msg._id && (
                <div className="pm-card-actions" data-testid={`pm-actions-${msg._id}`}>
                  <button
                    className="pm-btn pm-btn-approve"
                    onClick={() => handleApprove(msg)}
                    disabled={processing === msg._id}
                    data-testid={`pm-btn-approve-${msg._id}`}
                  >
                    {processing === msg._id ? 'Sending...' : 'Approve & Send'}
                  </button>
                  <button
                    className="pm-btn pm-btn-edit"
                    onClick={() => openEditor(msg)}
                    disabled={processing === msg._id}
                    data-testid={`pm-btn-edit-${msg._id}`}
                  >
                    Edit & Approve
                  </button>
                  <button
                    className="pm-btn pm-btn-reject"
                    onClick={() => handleReject(msg)}
                    disabled={processing === msg._id}
                    data-testid={`pm-btn-reject-${msg._id}`}
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          ))}
          <SeeMoreButton hasMore={pmPager.hasMore} remaining={pmPager.remaining} onClick={pmPager.showMore} testid="pending-messages-full" />
        </div>
      )}
    </div>
  );
};

export default PendingMessages;
